import styled from '@emotion/styled';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { RawBookingConfig } from '../../../api/admin/bookingConfig.api';
import SelectBase from '../../../components/Core/SelectBase/SelectBase';
import SelectField from '../../../components/Core/SelectField';
import Label from '../../../components/Core/Label/Label';
import { FormObject, Option } from '../../../hook/useBuildForm';
import { paymentsOptions as getPaymentsOptions } from '../../../parameters/constants/payments';
import { CustomFieldProps } from '../DepositConfig/depositConfig.form';

export function transformRawBookingConfig(rawBookingConfig?: RawBookingConfig) {
  if (rawBookingConfig) {
    const { paymentsMeans, options, langs, ...other } = rawBookingConfig;
    return {
      defaultValues: {
        paymentsMeans,
        ...other,
      },
      options,
    };
  }

  return undefined;
}

type ExtraFormProps = {
  rawOptions?: Record<string, string[]>;
  rawPaymentMeans?: Record<string, string | boolean | null>;
};

function ExtraFormByPaymentMean(props: ExtraFormProps & Partial<CustomFieldProps>) {
  const { t } = useTranslation('admin/bookingConfig');
  const { rawOptions, control, rawPaymentMeans, unregister, setValue } = props;
  const paymentsMeans: Record<string, string> | undefined = useWatch({ name: 'paymentsMeans', control });
  const [paymentMeansSelected, setPaymentMeansSelected] = useState<Option[]>([]);

  useEffect(() => {
    setPaymentMeansSelected(
      getPaymentsOptions(true, t).filter(
        (option) =>
          rawPaymentMeans !== undefined &&
          Object.keys(rawPaymentMeans).includes(option.value) &&
          rawPaymentMeans[option.value] !== false
      )
    );
  }, [rawPaymentMeans, t]);

  /*const onChange = (newValue: unknown) => {
    Object.keys(paymentsMeans || {}).forEach((paymentMean) => {
      if (!(newValue as Option[]).map((option) => option.value).includes(paymentMean)) {
        control?.unregister('paymentsMeans.'.concat(paymentMean), {
          keepValue: false,
          keepDefaultValue: false,
        });
      }
    });

    setPaymentMeansSelected(newValue as Option[]);
  };*/
  const onChange = useCallback(
    (newValue: unknown) => {
      (newValue as Option[])
        .map((option) => option.value)
        .forEach((optionSelected) => {
          if (
            // !Object.keys(paymentsMeans || {}).includes(optionSelected as string) &&
            !rawOptions?.[optionSelected as string] ||
            rawOptions?.[optionSelected as string].length === 0
          ) {
            setValue?.('paymentsMeans.'.concat(optionSelected as string), { value: true });
            // register?.('paymentsMeans.'.concat(optionSelected as string), { value: true });
            // setValue?.('paymentsMeans', copy, { shouldDirty: true, shouldTouch: true });
          }
        });

      const copy = { ...paymentsMeans };
      Object.keys(paymentsMeans || {}).forEach((paymentMean) => {
        if (!(newValue as Option[]).map((option) => option.value).includes(paymentMean)) {
          delete copy[paymentMean];
          /*unregister?.('paymentsMeans.'.concat(paymentMean), {
            keepValue: false,
            keepDefaultValue: false,
            keepDirty: false,
            keepTouched: false,
            keepDirtyValues: false,
          });*/
          unregister?.('paymentsMeans');
          setValue?.('paymentsMeans', copy, { shouldDirty: true, shouldTouch: true });
        }
      });
      /*reset?.();
      console.log(newValue);*/
      /*(newValue as Option[])
            .map((option) => option.value)
            .forEach((paymentMean) => {
              if (!rawOptions?.[paymentMean as string]) {
                setValue?.('paymentsMeans.'.concat(paymentMean as string), true, {
                  shouldDirty: true,
                  shouldTouch: true,
                });
              }
            });*/

      setPaymentMeansSelected(newValue as Option[]);
    },
    [paymentsMeans, control, setValue, unregister, rawOptions]
  );

  // console.log(getValues?.());
  const paymentsOptions = useMemo(() => {
    return getPaymentsOptions(true, t);
  }, [t]);

  return (
    <ExtraFormRoot>
      <div>
        <Label>{t('booking_active')}</Label>
        <PaymentMeansSelect value={paymentMeansSelected} isMulti onChange={onChange} options={paymentsOptions} />
      </div>
      <Grid>
        {paymentMeansSelected
          .map((paymentMean) => paymentMean.value)
          ?.filter((paymentMeanSelected) => (rawOptions?.[paymentMeanSelected as string]?.length || 0) > 0)
          .map((paymentMean, index) =>
            rawOptions?.[paymentMean as string].length || 0 > 0 ? (
              <Controller
                key={(paymentMean as string).concat(index.toString())}
                control={control}
                name={'paymentsMeans.'.concat(paymentMean as string)}
                render={({ fieldState: { error }, field: { onChange, value } }) => {
                  const options =
                    rawOptions?.[paymentMean as string].map((value) => ({ value, label: t(value) })) || [];

                  return (
                    <SelectField
                      label={t(paymentMean as string)}
                      placeholder={t(paymentMean as string)}
                      error={error}
                      onChange={onChange}
                      options={options}
                      value={options.find((option) => option.value === value) || null}
                    />
                  );
                }}
              />
            ) : null
          )}
      </Grid>
    </ExtraFormRoot>
  );
}

const PaymentMeansSelect = styled(SelectBase)``;

const Grid = styled.div`
  display: grid;
  gap: 16px;
`;

const ExtraFormRoot = styled(Grid)`
  grid-column: 1 / 2 span;
  grid-template-columns: repeat(2, 1fr);
`;

function useBookingForm(
  rawPaymentMeans?: Record<string, string | boolean | null>,
  rawOptions?: Record<string, string[]>
): FormObject {
  const { t } = useTranslation('admin/bookingConfig');

  return [
    {
      colsNumber: 2,
      fields: [
        {
          type: 'checkbox',
          label: t('btn_booking'),
          name: 'enabled',
        },
        {
          type: 'custom',
          component: <ExtraFormByPaymentMean rawPaymentMeans={rawPaymentMeans} rawOptions={rawOptions} />,
          name: '',
          label: '',
        },
        {
          type: 'text',
          name: 'confirmationText.fr',
          label: t('confirmation_txt_fr'),
          columnStart: 1,
        },
        {
          type: 'text',
          name: 'confirmationText.en',
          label: t('confirmation_txt_en'),
        },
        {
          type: 'text',
          name: 'paymentInfoText.fr',
          label: t('booking_payment_info_fr'),
        },
        {
          type: 'text',
          name: 'paymentInfoText.en',
          label: t('booking_payment_info_en'),
        },
      ],
    },
  ];
}

export default useBookingForm;
